import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";

const BusinessOrdersPage = () => {
  /* ------------------------------------------------------------------ */
  /* state */
  const [businessOrders, setBusinessOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState(null);

  // UI-filter state
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("Pending");

  const navigate = useNavigate();

  /* ------------------------------------------------------------------ */
  /* data fetch */
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(
          "https://api.vyapakprint.com/api/businessOrders/getAllBusinessOrders",
        );
        setBusinessOrders(data.BusinessOrders || []);
      } catch (err) {
        console.error(err);
        setError("Failed to fetch business orders");
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  /* ------------------------------------------------------------------ */
  /* helpers */
  const handleViewDetails = (id) => navigate(`/businessOrders-details/${id}`);

  const handleDownload = async (fileUrl) => {
    try {
      const response = await fetch(fileUrl);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      const fileName = fileUrl.split("/").pop() || "download";
      link.download = fileName;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error("Download failed:", error);
      const link = document.createElement("a");
      link.href = fileUrl;
      link.download = "";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  };

  const getLastStatusUpdate = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].changedAt;
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: "UTC",
    });
  };

  const getRemarksUpdate = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].remark;
  };

  const getLastStatusImage = (statusHistory) => {
    if (!statusHistory || statusHistory.length === 0) return null;
    return statusHistory[statusHistory.length - 1].image;
  };

  const getCategoryName = (order) => {
    return (
      order?.categoryName ||
      order?.category?.name ||
      order?.category?.title ||
      "N/A"
    );
  };

  /* ------------------------------------------------------------------ */
  /* derived list (memoised) */
  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    return businessOrders.filter((order) => {
      if (statusFilter !== "ALL" && order.status !== statusFilter) return false;

      if (!term) return true;

      const user = order.userDetails || {};
      const fields = [
        order._id,
        order.status,
        order.firmName,
        order.productName,
        getCategoryName(order),
        user.businessName,
        user.contactPerson,
        user.mobile,
      ];

      return fields.some((f) =>
        (f || "").toString().toLowerCase().includes(term),
      );
    });
  }, [businessOrders, searchTerm, statusFilter]);

  /* ------------------------------------------------------------------ */
  /* render */
  if (loading)
    return (
      <div className="flex justify-center items-center h-64">Loading...</div>
    );

  if (error) return <div className="text-red-600 text-center">{error}</div>;

  return (
    <div className="container mx-auto p-6">
      <h1 className="text-3xl font-bold mb-6">Business Orders</h1>

      <div className="flex flex-col md:flex-row md:items-end gap-4 mb-5">
        <div className="flex flex-col">
          <label htmlFor="search" className="text-sm font-medium mb-1">
            Search
          </label>
          <div className="flex">
            <input
              id="search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Order ID, Business, Contact, Product, Category, Firm..."
              className="w-72 px-3 py-2 border rounded-l-md focus:outline-none focus:ring-1 focus:ring-blue-500"
            />
            <button
              onClick={() => setSearchTerm(searchTerm)}
              className="px-4 py-2 bg-blue-500 text-white rounded-r-md hover:bg-blue-600"
            >
              Search
            </button>
          </div>
        </div>

        <div className="flex flex-col">
          <label htmlFor="status" className="text-sm font-medium mb-1">
            Status
          </label>
          <select
            id="status"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="w-40 px-3 py-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500"
          >
            <option value="ALL">All</option>
            <option value="Pending">Pending</option>
            <option value="Printing">Printing</option>
            <option value="Packing">Packing</option>
            <option value="Dispatched">Dispatched</option>
            <option value="Cancelled">Cancelled</option>
          </select>
        </div>

        {(searchTerm || statusFilter !== "ALL") && (
          <button
            onClick={() => {
              setSearchTerm("");
              setStatusFilter("ALL");
            }}
            className="self-start md:self-end px-4 py-2 bg-gray-200 rounded hover:bg-gray-300"
          >
            Reset
          </button>
        )}
      </div>

      <div className="overflow-auto bg-white rounded-lg shadow-md border">
        <table className="min-w-[1450px] w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="py-3 px-4 text-left">#</th>
              <th className="py-3 px-4 text-left">Order No</th>
              <th className="py-3 px-4 text-left">Business Name</th>
              <th className="py-3 px-4 text-left">Mobile</th>
              <th className="py-3 px-4 text-left">Category</th>
              <th className="py-3 px-4 text-left">Product Name</th>
              
              <th className="py-3 px-4 text-left">Firm Name</th>
              <th className="py-3 px-4 text-center">Quantity</th>
              <th className="py-3 px-4 text-center">Status</th>
              <th className="py-3 px-4 text-center">Remarks</th>
              <th className="py-3 px-4 text-center">Order Date</th>
              <th className="py-3 px-4 text-center">Last Updated</th>
              <th className="py-3 px-4 text-center">File</th>
              <th className="py-3 px-4 text-center">Actions</th>
            </tr>
          </thead>

          <tbody>
            {filteredOrders.length ? (
              filteredOrders.map((order, idx) => {
                const user = order.userDetails || {};
                const lastUpdate = getLastStatusUpdate(order.statusHistory);
                const remarks = getRemarksUpdate(order.statusHistory);
                const statusImage = getLastStatusImage(order.statusHistory);
                const categoryName = getCategoryName(order);

                return (
                  <tr key={order._id} className="border-t hover:bg-gray-50">
                    <td className="py-3 px-4">{idx + 1}</td>
                    <td className="py-3 px-4 font-mono text-xs">
                      {order._id || "N/A"}
                    </td>
                    <td className="py-3 px-4 font-medium">
                      {user.businessName || "N/A"}
                    </td>
                    <td className="py-3 px-4">{user.mobile || "N/A"}</td>
                     <td className="py-3 px-4">{categoryName}</td>
                    <td className="py-3 px-4 font-medium text-blue-600">
                      {order.productName || "N/A"}
                    </td>
                   
                    <td className="py-3 px-4">{order.firmName || "N/A"}</td>
                    <td className="py-3 px-4 text-center">
                      {order.quantity || 0}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${
                          order.status === "Pending"
                            ? "bg-yellow-100 text-yellow-800"
                            : order.status === "Cancelled"
                              ? "bg-red-100 text-red-800"
                              : order.status === "Dispatched"
                                ? "bg-green-100 text-green-800"
                                : order.status === "Printing"
                                  ? "bg-blue-100 text-blue-800"
                                  : order.status === "Packing"
                                    ? "bg-purple-100 text-purple-800"
                                    : "bg-gray-100 text-gray-800"
                        }`}
                      >
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center text-xs">
                      <div className="flex items-center justify-center gap-2">
                        <span>{remarks || "N/A"}</span>
                        {statusImage && (
                          <button
                            onClick={() => setModalImage(statusImage)}
                            className="text-blue-500 hover:text-blue-700 text-xs underline"
                          >
                            View Image
                          </button>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-center text-xs">
                      {formatDateTime(order.orderDate)}
                    </td>
                    <td className="py-3 px-4 text-center text-xs">
                      {lastUpdate ? formatDateTime(lastUpdate) : "N/A"}
                    </td>
                    <td className="py-3 px-4 text-center">
                      {order.file ? (
                        <button
                          onClick={() => handleDownload(order.file)}
                          className="text-blue-600 hover:text-blue-800 hover:underline text-xs"
                          title="Download file"
                        >
                          📄 Download
                        </button>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <button
                        onClick={() => handleViewDetails(order._id)}
                        className="bg-blue-500 text-white px-3 py-1 rounded text-xs hover:bg-blue-600 transition-colors"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="14" className="py-8 text-center text-gray-500">
                  No matching orders found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        Showing {filteredOrders.length} of {businessOrders.length} orders
      </div>

      {modalImage && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-4 max-w-4xl max-h-[90vh] overflow-auto">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold">Status Image</h3>
              <button
                onClick={() => setModalImage(null)}
                className="text-gray-500 hover:text-gray-700 text-xl font-bold"
              >
                ×
              </button>
            </div>
            <img
              src={modalImage}
              alt="Status update"
              className="max-w-full max-h-[70vh] object-contain mx-auto"
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default BusinessOrdersPage;
